// @ts-nocheck
/**
 * Template V2 — Item UI Actions catalog (DEC-070).
 *
 * Shared per-element context-menu / header Pack configuration.
 * Runtime resolves via ElementDefinition.itemUiActionsId → catalog entry.
 */

import type { LocalizedText } from './metadata';

/** Extensible menu surface identifier. */
export type MenuSurface = 'list' | 'containd' | 'placeholder' | 'marquee';

export type BuiltinActionId =
  // List
  | 'list.edit'
  | 'list.view'
  | 'list.rearrange'
  | 'list.pack'
  | 'list.packPlaceholders'
  | 'list.statistics'
  | 'list.reminder'
  | 'list.labels'
  | 'list.duplicate'
  | 'list.copy'
  | 'list.cut'
  | 'list.delete'
  | 'list.toggleLocalReadOnly'
  | 'list.export'
  | 'list.exportForEdit'
  | 'list.bulkCsvExport'
  | 'list.bulkEditTable'
  | 'list.importFromEdit'
  | 'list.itemActions'
  | 'list.listActions'
  | 'list.importExport'
  // Containd single-item
  | 'containd.view'
  | 'containd.enter'
  | 'containd.edit'
  | 'containd.moveUp'
  | 'containd.copy'
  | 'containd.duplicate'
  | 'containd.cut'
  | 'containd.delete'
  | 'containd.pack'
  | 'containd.packPlaceholders'
  | 'containd.createPlaceholder'
  | 'containd.toggleContainer'
  | 'containd.statistics'
  | 'containd.labels'
  | 'containd.reprocessAI'
  | 'containd.reminder'
  | 'containd.export'
  | 'containd.shareImage'
  | 'containd.copyPaste'
  | 'containd.organize'
  | 'containd.tools'
  // Runtime-injected (not authorable in trees; present in resolved output)
  | 'containd.share'
  | 'containd.publishWebView'
  | 'list.share'
  | 'list.publishWebView'
  | 'list.deleteReadOnly'
  // Authorable Arrange / style (also ensured at resolve — Style toolbar cannot be omitted)
  | 'containd.arrange'
  | 'containd.bringToFront'
  | 'containd.sendToBack'
  | 'containd.toggleLock'
  | 'containd.toggleCanvasTitleEdit'
  | 'containd.openStyleToolbar'
  // Placeholder
  | 'placeholder.goToOriginal'
  | 'placeholder.editOriginal'
  | 'placeholder.viewOriginal'
  | 'placeholder.switchWithOriginal'
  | 'placeholder.delete'
  | 'placeholder.moveUp'
  | 'placeholder.duplicate'
  // Marquee (also MarqueeActionId — shared registry)
  | 'marquee.copy'
  | 'marquee.cut'
  | 'marquee.delete'
  | 'marquee.bringToFront'
  | 'marquee.sendToBack'
  | 'marquee.toggleLock'
  | 'marquee.autoArrange'
  | 'marquee.reprocessAI'
  | 'marquee.labels';

/** Existing marquee actions only — no custom entries / no tree. */
export type MarqueeActionId = Extract<BuiltinActionId, `marquee.${string}`>;

export type MenuTreeNode =
  | { kind: 'builtin'; id: string; actionId: BuiltinActionId }
  | { kind: 'submenu'; id: string; label: LocalizedText; children: MenuTreeNode[] }
  | {
      kind: 'custom';
      id: string;
      label: LocalizedText;
      /** scriptId must be in element or attached-class API exports */
      api: { scriptId: string; classId?: string };
      /** Optional closed icon id; omitted → default script icon. */
      iconId?: string;
    }
  | { kind: 'separator'; id: string };

export interface ItemUiActionsHeaderFlags {
  list?: { pack?: boolean; packPlaceholders?: boolean };
  containd?: { pack?: boolean };
}

/** Catalog entry keyed by id; shared across elements via itemUiActionsId. */
export interface ItemUiActionsDefinition {
  id: string;
  displayName?: LocalizedText;
  header?: ItemUiActionsHeaderFlags;
  listContextMenu?: MenuTreeNode[];
  containdContextMenu?: MenuTreeNode[];
  placeholderContextMenu?: MenuTreeNode[];
  /**
   * When this element participates in a marquee multi-selection, these flags
   * can hide existing marquee actions. Omitted / true = enabled.
   */
  marqueeContextMenu?: Partial<Record<MarqueeActionId, boolean>>;
}

/** Basics that must remain present (movable, never removable) per surface. */
export const LIST_MENU_BASICS: readonly BuiltinActionId[] = [
  'list.edit',
  'list.view',
] as const;

export const CONTAIND_MENU_BASICS: readonly BuiltinActionId[] = [
  'containd.view',
  'containd.enter',
  'containd.edit',
] as const;

export const PLACEHOLDER_MENU_BASICS: readonly BuiltinActionId[] = [
  'placeholder.goToOriginal',
  'placeholder.editOriginal',
  'placeholder.viewOriginal',
  'placeholder.switchWithOriginal',
] as const;

/**
 * Builtin actionIds that must not appear in authored trees (runtime-injected only).
 * KEEP THIS LIST HOST-INJECTED ONLY (share / publish web view / read-only-delete).
 * Do NOT add Arrange / Style toolbar / z-order / lock — `shouldShowBuiltin` hides these
 * unless `allowRuntimeInjected: true`, which emptied the Arrange submenu in production.
 * See docs/TEMPLATE_V2_ITEM_UI_ACTIONS.md → Invariants.
 */
export const RUNTIME_INJECTED_ACTION_IDS: readonly BuiltinActionId[] = [
  'list.share',
  'list.publishWebView',
  'list.deleteReadOnly',
  'containd.share',
  'containd.publishWebView',
] as const;

/**
 * Always present under the Containd Arrange submenu — authors may include Arrange,
 * but cannot omit Style toolbar (host re-injects it into Arrange if missing).
 * Product lock: there is no supported way to exclude Style toolbar from Arrange.
 * See docs/TEMPLATE_V2_ITEM_UI_ACTIONS.md → Invariants.
 */
export const ARRANGE_REQUIRED_ACTION_IDS: readonly BuiltinActionId[] = [
  'containd.openStyleToolbar',
] as const;

/** Stable submenu node id for Containd Arrange (authored + injected). */
export const CONTAIND_ARRANGE_SUBMENU_ID = 'containd-arrange' as const;
